import { FC, useCallback, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useStore } from '../../../hooks/useStore';
import { shallow } from 'zustand/shallow';
import { AdElementText, BrandVariable } from '@adflow/types';
import { Flex, Box } from '@chakra-ui/react';
import {
  buildAvailableFonts,
  extractStretchFromFamily,
  getVariantLabel,
  normalizeVariantLabel,
} from './utils/fontUtils';
import { FontFamilySelect } from './components/FontFamilySelect';
import { FontVariantSelect } from './components/FontVariantSelect';
import BrandBadge from '../BrandBadge';

type Props = {
  selectedElement: AdElementText;
};

const FontSelectionControls: FC<Props> = ({ selectedElement }) => {
  const store = useStore(
    (state) => ({
      updateElementData: state.updateElementData,
      customFonts: state.customFonts,
      brandKit: state.brandKit,
    }),
    shallow,
  );

  const currentFamily = selectedElement.data.style.fontFamily || '';
  const currentVariantLabel = selectedElement.data.style.fontVariant || '';
  const currentWeight = selectedElement.data.style.fontWeight || 400;
  const currentStyle = selectedElement.data.style.fontStyle || 'normal';
  const currentStretch = selectedElement.data.style.fontStretch || 'normal';

  const availableFontsGrouped = useMemo(() => {
    return buildAvailableFonts({
      customFonts: store.customFonts,
      originalFamily: currentFamily,
      originalStyle: currentStyle,
      originalWeight: currentWeight,
      originalStretch: currentStretch,
    });
  }, [store.customFonts, currentFamily, currentStyle, currentWeight, currentStretch]);

  const findFontNameByFontFamilyId = useCallback(
    (fontFamilyId: string | undefined) => {
      if (!fontFamilyId) return null;
      // TODO: For whoever does the standard fonts ticket, you should also find the brand font from the standard ones here.
      const customFont = store.customFonts.find((f) => f.fontFamilyId === fontFamilyId);
      if (customFont) {
        return customFont.fontFamily;
      }
      return null;
    },
    [store.customFonts],
  );

  const brandFontType = useMemo(() => {
    if (!store.brandKit?.fonts) return null;

    if (
      currentFamily === BrandVariable.PRIMARY_FONT ||
      currentFamily === BrandVariable.SECONDARY_FONT
    ) {
      return currentFamily as BrandVariable;
    }

    return null;
  }, [store.brandKit, currentFamily]);

  const resolvedCurrentFamily = useMemo(() => {
    if (currentFamily === BrandVariable.PRIMARY_FONT) {
      return (
        findFontNameByFontFamilyId(store.brandKit?.fonts?.primary?.fontFamilyId) || 'Primary Font'
      );
    } else if (currentFamily === BrandVariable.SECONDARY_FONT) {
      return (
        findFontNameByFontFamilyId(store.brandKit?.fonts?.secondary?.fontFamilyId) ||
        findFontNameByFontFamilyId(store.brandKit?.fonts?.primary?.fontFamilyId) ||
        'Secondary Font'
      );
    }
    return currentFamily;
  }, [store.brandKit, currentFamily, findFontNameByFontFamilyId]);

  // Find the currently selected font group
  const selectedFontGroup = useMemo(() => {
    return availableFontsGrouped.find((f) => f.family === resolvedCurrentFamily);
  }, [availableFontsGrouped, resolvedCurrentFamily]);

  const normalizedCustomVariants = useMemo(() => {
    const { baseFamily } = extractStretchFromFamily(resolvedCurrentFamily);
    return store.customFonts
      .map((font) => {
        const { baseFamily: fontBaseFamily, stretch: fontFamilyStretch } = extractStretchFromFamily(
          font.fontFamily,
        );
        if (fontBaseFamily !== baseFamily) return null;

        const fontWeight = font.weight ?? 400;
        const fontStyle = font.style && font.style.trim() !== '' ? font.style : 'normal';
        const fontStretch = font.stretch && font.stretch.trim() !== '' ? font.stretch : 'normal';
        const finalFontStretch = fontStretch !== 'normal' ? fontStretch : fontFamilyStretch;

        return {
          weight: fontWeight,
          style: fontStyle,
          stretch: finalFontStretch,
        };
      })
      .filter(Boolean) as Array<{ weight: number; style: string; stretch: string }>;
  }, [store.customFonts, resolvedCurrentFamily]);

  const normalizedCustomVariantLabels = useMemo(() => {
    // For custom fonts, use exact family name without normalization
    return new Set(
      store.customFonts
        .map((font) => {
          // Match by exact family name
          if (font.fontFamily !== resolvedCurrentFamily) return null;

          const fontWeight = font.weight ?? 400;
          const fontStyle = font.style && font.style.trim() !== '' ? font.style : 'normal';
          const label = normalizeVariantLabel(getVariantLabel(fontWeight, fontStyle, font.variant));

          return label || null;
        })
        .filter(Boolean) as string[],
    );
  }, [store.customFonts, resolvedCurrentFamily]);

  const isCustomFamily = useMemo(
    () => normalizedCustomVariants.length > 0,
    [normalizedCustomVariants.length],
  );

  const isMissingVariant = useMemo(() => {
    if (!resolvedCurrentFamily || !isCustomFamily) return false;

    const normalizedLabel = normalizeVariantLabel(currentVariantLabel);
    if (normalizedLabel) {
      return !normalizedCustomVariantLabels.has(normalizedLabel);
    }

    const normalizedWeight = currentWeight || 400;
    const normalizedStyle = currentStyle || 'normal';
    const normalizedStretch = currentStretch || 'normal';
    const { stretch: familyStretch } = extractStretchFromFamily(resolvedCurrentFamily);
    const finalStretch = normalizedStretch !== 'normal' ? normalizedStretch : familyStretch;

    const exactMatch = normalizedCustomVariants.some(
      (variant) =>
        variant.weight === normalizedWeight &&
        variant.style === normalizedStyle &&
        variant.stretch === finalStretch,
    );

    if (exactMatch) return false;

    const ignoreStretchMatches = normalizedCustomVariants.filter(
      (variant) => variant.weight === normalizedWeight && variant.style === normalizedStyle,
    );

    return ignoreStretchMatches.length !== 1;
  }, [
    resolvedCurrentFamily,
    currentVariantLabel,
    currentWeight,
    currentStyle,
    currentStretch,
    isCustomFamily,
    normalizedCustomVariants,
    normalizedCustomVariantLabels,
  ]);

  const missingVariantLabel = useMemo(() => {
    if (!isMissingVariant) return '';
    const normalizedLabel = normalizeVariantLabel(currentVariantLabel);
    if (normalizedLabel) return currentVariantLabel.trim();
    const normalizedWeight = currentWeight || 400;
    const normalizedStyle = currentStyle || 'normal';
    return getVariantLabel(normalizedWeight, normalizedStyle);
  }, [currentVariantLabel, currentWeight, currentStyle, isMissingVariant]);

  const handleFamilyChange = (family: string) => {
    // Find the new family group
    const newGroup = availableFontsGrouped.find((f) => f.family === family);

    // Default to Regular (400, normal, normal stretch) or the first available variant
    let newWeight = 400;
    let newStyle = 'normal';
    let newStretch = 'normal';
    let newVariantLabel = getVariantLabel(newWeight, newStyle);

    if (newGroup && newGroup.stretchGroups.length > 0) {
      // Try to find normal stretch group first
      const normalStretchGroup = newGroup.stretchGroups.find((sg) => sg.stretch === 'normal');
      const targetGroup = normalStretchGroup || newGroup.stretchGroups[0];

      // Try to find Regular/Normal variant
      const regular = targetGroup.variants.find((v) => v.weight === 400 && v.style === 'normal');
      if (regular) {
        newWeight = regular.weight;
        newStyle = regular.style;
        newStretch = regular.stretch;
        newVariantLabel = regular.label;
      } else if (targetGroup.variants.length > 0) {
        // Fallback to the first variant in the target group
        const fallbackVariant = targetGroup.variants[0];
        newWeight = fallbackVariant.weight;
        newStyle = fallbackVariant.style;
        newStretch = fallbackVariant.stretch;
        newVariantLabel = fallbackVariant.label;
      }
    }

    // If the new family is a brand font, update the font to primary or secondary
    if (store.brandKit) {
      if (family === findFontNameByFontFamilyId(store.brandKit?.fonts?.primary?.fontFamilyId)) {
        family = BrandVariable.PRIMARY_FONT;
      } else if (
        family === findFontNameByFontFamilyId(store.brandKit?.fonts?.secondary?.fontFamilyId)
      ) {
        family = BrandVariable.SECONDARY_FONT;
      }
    }

    store.updateElementData(selectedElement.id, {
      ...selectedElement.data,
      style: {
        ...selectedElement.data.style,
        fontFamily: family,
        fontWeight: newWeight,
        fontStyle: newStyle,
        fontStretch: newStretch,
        fontVariant: newVariantLabel,
      },
    });
  };

  const handleVariantChange = (next: {
    weight: number;
    style: string;
    stretch: string;
    label: string;
  }) => {
    const weight = next.weight;
    const style = next.style;
    const stretch = next.stretch && next.stretch.trim() !== '' ? next.stretch : 'normal';
    const label =
      next.label && next.label.trim() !== '' ? next.label : getVariantLabel(weight, style);

    store.updateElementData(selectedElement.id, {
      ...selectedElement.data,
      style: {
        ...selectedElement.data.style,
        fontWeight: weight,
        fontStyle: style,
        fontStretch: stretch,
        fontVariant: label,
      },
    });
  };

  return (
    <Box mt={5}>
      <Flex alignItems="center" gap={2}>
        <Typography variant="h4" fontWeight="bold">
          Font
        </Typography>
        {brandFontType && <BrandBadge variableType={'none'} />}
      </Flex>
      <Flex direction="column" gap={2} mt={2}>
        <FontFamilySelect
          value={resolvedCurrentFamily}
          fonts={availableFontsGrouped}
          customFonts={store.customFonts}
          primaryFont={findFontNameByFontFamilyId(store.brandKit?.fonts?.primary?.fontFamilyId)}
          secondaryFont={findFontNameByFontFamilyId(store.brandKit?.fonts?.secondary?.fontFamilyId)}
          onChange={handleFamilyChange}
        />

        <FontVariantSelect
          currentWeight={currentWeight}
          currentStyle={currentStyle}
          currentStretch={currentStretch}
          fontGroup={selectedFontGroup}
          missingVariant={isMissingVariant}
          missingVariantLabel={missingVariantLabel}
          disabled={!currentFamily}
          onChange={handleVariantChange}
        />
      </Flex>
    </Box>
  );
};

FontSelectionControls.displayName = 'FontSelectionControls';

export default FontSelectionControls;
export type { Props as FontSelectionControlsProps };
